import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FileUploadService } from '@app/shared/services/file-upload/file-upload.service';
import { LoadingService } from '@app/shared/services/loading.service';
import { TokenService } from '@app/shared/services/token/token.service';
import { Convert } from '@app/shared/utils/ConvertMimeTypes';
import Utils from '@app/shared/utils/utils';
import { SetUserInfo } from '@app/store/auth/auth.actions';
import { AuthState } from '@app/store/auth/auth.state';
import { TvfRequestsService } from '@app/tvf/services/tvf-requests.service';
import { Store } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';
import { EMPTY } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';

export interface IMobileUploadFileParams {
  idTrade: string;
  idToken: string;
  evidences?: any;
}

@Component({
  selector: 'app-upload-images-mobile',
  templateUrl: './upload-images-mobile.component.html',
  styleUrls: ['./upload-images-mobile.component.sass'],
})
export class UploadImagesMobileComponent implements OnInit {
  file: any;
  generatePreSignedApiService: string;
  registerUploadApiService: string;
  imagePreviewUrlList: string[] = [];
  deletingPhotoUrls = new Set<string>();
  params: IMobileUploadFileParams;
  callFinishScreeningPage: boolean = false;
  idTrade: any;
  ips: boolean = false;
  evidences: any[] = [];
  isLoading: boolean;
  completedWork: boolean = false;
  laudo: boolean = false;
  productDivergenceEvidence = false;
  token: string | boolean;

  constructor(
    private tokenService: TokenService,
    private route: ActivatedRoute,
    private tvfRequest: TvfRequestsService,
    private store: Store,
    private uploadService: FileUploadService,
    private toastrService: ToastrService,
    private loading: LoadingService
  ) {}

  ngOnInit(): void {
    this.loading.forceLoading = true;
    this.completedWork = false;
    this.isLoading = true;
    this.route.queryParams.pipe(take(1)).subscribe(async ({ t, i, ips, evidences, laudo, token, productDivergenceEvidence }) => {
      this.token = token ? token : '';

      if (!t || !i) {
        console.error('Parâmetros ausentes.');
        return;
      }

      try {
        this.idTrade = t;
        this.ips = ips === 'true';
        this.laudo = laudo === 'true';
        this.productDivergenceEvidence = !this.ips && productDivergenceEvidence === 'true';

        let decodedEvidences = [];
        if (evidences) {
          const jsonString = decodeURIComponent(escape(window.atob(evidences)));
          decodedEvidences = JSON.parse(jsonString);
          this.evidences = decodedEvidences;
        }

        this.generatePreSignedApiService = this.ips
          ? 'COLLECT.postGeneratePresignedUrl'
          : 'COLLECT_TVF.postGeneratePresignedUrl';
        this.registerUploadApiService = this.ips
          ? 'COLLECT.postRegisterUploadedMedia'
          : 'COLLECT_TVF.postRegisterUploadedMedia';

        let { token } = this.store.selectSnapshot(AuthState);

        if (!token) {
          const tokenResponse = await this.tokenService
            .get(i, this.ips ? 'COLLECT.getToken' : 'COLLECT_TVF.getToken')
            .pipe(take(1))
            .toPromise();
          token = tokenResponse?.token ?? tokenResponse?.Token;
          this.handleTokenResponse(token);
        }

        await this.loadGallery(t);
        this.params = { idTrade: t, idToken: i, evidences: decodedEvidences };
        this.isLoading = false;
      } catch (error) {
        console.error('Erro ao obter dados:', error);
        this.isLoading = false;
      }
    });
  }

  private async loadGallery(idTrade: string = this.idTrade): Promise<void> {
    const { stiImages, images } = await this.uploadService
      .getGalleryByTradeId(
        idTrade,
        this.ips ? 'COLLECT.getTradeGallery' : 'COLLECT_TVF.getTradeGallery'
      )
      .pipe(take(1))
      .toPromise();

    this.imagePreviewUrlList = stiImages || [];

    if (images?.length) {
      this.evidences = this.evidences.map((evidence) => {
        const matchedImageUrl = images.find((imgUrl) => {
          const fileName = this.extractFileName(imgUrl);
          const [nameWithoutToken, tokenPart] = fileName.split('-');
          return (
            nameWithoutToken === evidence.description && tokenPart && tokenPart === this.token
          );
        });

        if (matchedImageUrl) {
          return { ...evidence, src: matchedImageUrl };
        }

        return evidence;
      });
    }
  }

  verifyEvidences(): boolean {
    return this.evidences.some((evidence) => !evidence.src || evidence.src.trim() === '');
  }

  uploadLaudoPack(): void {
    this.loading.show();

    const uploadEvidences = async () => {
      try {
        for (const evidence of this.evidences) {
          const mimeType = evidence.src.split(';')[0].split(':')[1];
          const fileName = `${this.params.idTrade}/${Utils.randomFilenameGenerator(
            `laudo-${evidence.description}-${this.token}`,
            Convert.getExtension(mimeType)[0]
          )}`;

          const payload = {
            idTrade: this.params.idTrade,
            filename: fileName,
            contentType: mimeType,
          };

          const response = await this.uploadService
            .postGeneratePresignedUrlUpload(payload, this.generatePreSignedApiService)
            .toPromise();

          const file = await this.convertSrcToFile(evidence.src, fileName);

          await this.uploadService.uploadFile(file, response.url).toPromise();
          await this.uploadService
            .updateTradeGallery(
              { idTrade: this.params.idTrade, filename: fileName },
              this.registerUploadApiService
            )
            .toPromise();
        }

        this.toastrService.success('Todos os uploads enviados com sucesso.');
      } catch (error) {
        this.toastrService.error(
          'Não foi possível enviar todos os arquivos. Por favor, tente novamente.'
        );
      } finally {
        this.loading.hide();
        this.completedWork = true;
      }
    };

    uploadEvidences();
  }

  private convertSrcToFile(src: string, fileName: string): Promise<File | null> {
    return new Promise((resolve, reject) => {
      if (src.startsWith('data:')) {
        const arr = src.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        const n = bstr.length;
        const u8arr = new Uint8Array(n);
        for (let i = 0; i < n; i++) {
          u8arr[i] = bstr.charCodeAt(i);
        }
        resolve(new File([u8arr], fileName, { type: mime }));
      } else {
        fetch(src)
          .then((response) => response.blob())
          .then((blob) => {
            resolve(new File([blob], fileName, { type: blob.type }));
          })
          .catch((error) => {
            reject(`Erro ao converter URL para File: ${error}`);
          });
      }
    });
  }

  extractFileName(url: string): string {
    const match = url.match(/laudo-(.*?)_/);
    return match ? match[1] : '';
  }

  private handleTokenResponse(token: any): void {
    if (token) {
      this.store.dispatch(new SetUserInfo({ token }));
    } else {
      console.error('Token inválido:', token);
    }
  }

  fileUp(event: Event, description?: string) {
    const target = event.target as HTMLInputElement;
    const file = target.files[0];

    const filename = `${this.params.idTrade}/${Utils.randomFilenameGenerator(
      this.productDivergenceEvidence
        ? `evidencia_produto_${this.params.idTrade}_mb`
        : !description ? 'laudo' : `laudo-${description}${this.token ? '-' + this.token : ''}`,
      Convert.getExtension(file.type)[0].toString()
    )}`;

    const payload = { idTrade: this.params.idTrade, filename, contentType: file.type };
    this.uploadService
      .postGeneratePresignedUrlUpload(payload, this.generatePreSignedApiService)
      .pipe(
        tap(() => this.loading.show()),
        switchMap((response: { url: string }) => this.uploadService.uploadFile(file, response.url)),
        switchMap(() =>
          this.uploadService.updateTradeGallery(
            { idTrade: this.params.idTrade, filename },
            this.registerUploadApiService
          )
        ),
        switchMap(() => this.loadGallery()),
        tap(() => {
          this.loading.hide();
          this.toastrService.success('Upload enviado com sucesso.');
        }),
        catchError(() => {
          this.loading.hide();
          this.toastrService.error(
            'Não foi possível enviar o arquivo. Por favor, tente novamente.'
          );
          return EMPTY;
        })
      )
      .subscribe();
  }

  async removePhotoAt(url: string): Promise<{ IdTrade: string; Path: string } | null> {
    if (this.deletingPhotoUrls.has(url)) {
      return null;
    }

    const match = url.split(/[?#]/)[0].match(/\/([^/]+)\/([^/]+)$/);
    if (!match || match.length !== 3) {
      console.error('URL inválida ou não corresponde ao padrão esperado.');
      return null;
    }

    const data = {
      IdTrade: match[1],
      Path: `${match[1]}/${match[2]}`,
    };

    this.deletingPhotoUrls.add(url);
    try {
      const remove = await this.uploadService
        .removeFilePathByTrade(data, 'COLLECT_TVF.deleteFilePathTrade')
        .pipe(take(1))
        .toPromise();

      this.imagePreviewUrlList = this.imagePreviewUrlList.filter((image) => image !== url);
      return remove;
    } catch (error) {
      this.toastrService.error('Não foi possível excluir a foto. Tente novamente.');
      return null;
    } finally {
      this.deletingPhotoUrls.delete(url);
    }
  }
}
